<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <TransitionRoot as="div" :show="open" ref="modalRef">
    <Dialog as="div" class="fixed z-30 inset-0 overflow-y-auto">
      <div class="flex justify-center min-h-screen text-center sm:block">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              relative
              inline-block
              bg-vice-dark-grey
              rounded-lg
              text-white text-left
              overflow-hidden
              shadow-xl
              transform
              transition-all
              align-middle
              max-w-md
              w-full
              p-6
            "
            ref="modalRef"
          >
            <XIcon
              data-cy="close-external-device-modal"
              class="w-5 top-6 absolute right-5 cursor-pointer"
              @click="cancelSelection"
              v-if="!['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd)"
            />
            <div>
              <div class="flex justify-between flex-wrap">
                <h2 class="w-full font-bold text-md mb-4">
                  {{
                    CONNECT_EXTERNAL_DEVICE_MODAL_LABELS[multiCameraDevicesStore.multiDeviceToAdd]
                      ?.modalTitle
                  }}
                </h2>
                <div class="relative block w-full">
                  <button
                    data-cy="open-devices-dropdown"
                    v-if="['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd)"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      w-full
                      rounded-md
                      border border-gray-300
                      shadow-sm
                      px-4
                      py-2
                      bg-white
                      text-sm
                      font-medium
                      text-gray-700
                      hover:bg-gray-50
                      focus:outline-none
                      focus:ring-2
                      focus:ring-offset-2
                      focus:ring-offset-gray-100
                      focus:ring-indigo-500
                    "
                    aria-expanded="true"
                    aria-haspopup="true"
                    @click="handleDropdownsState"
                  >
                    {{
                      isActiveCameraDevice
                        ? selectedVideoDeviceLabel
                        : `No ${multiCameraDevicesStore.multiDeviceToAdd} Selected`
                    }}
                    <ChevronDownIcon class="w-6 h-5"></ChevronDownIcon>
                  </button>
                  <!-- Camera List -->

                  <div
                    class="
                      origin-top-right
                      absolute
                      right-0
                      mt-2
                      w-full
                      z-10
                      rounded-md
                      shadow-lg
                      bg-white
                      ring-1 ring-black ring-opacity-5
                      divide-y divide-gray-100
                      focus:outline-none
                    "
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="menu-button"
                    tabindex="-1"
                    v-if="isVisibleCameraList"
                  >
                    <template v-for="(prop, index) in videoDevices" :key="prop.deviceId">
                      <div class="py-1" role="none">
                        <span
                          data-cy="select-device"
                          href="#"
                          class="text-gray-700 block pl-2 py-1 text-sm cursor-pointer"
                          role="menuitem"
                          tabindex="-1"
                          :id="`$Camera-${index}`"
                          @click="chooseCameraSource(prop)"
                          v-click-outside="
                            () => {
                              handleDropdownsState();
                            }
                          "
                        >
                          {{ prop.label }}
                          <span
                            v-if="
                              multiCameraDevicesStore.getConnectedCamerasLabels.includes(prop.label)
                            "
                            style="font-weight: bold; color: crimson"
                          >
                            (in use)
                          </span>
                        </span>
                      </div>
                    </template>
                  </div>

                  <!-- /End of Camera List -->

                  <div class="mt-4 flex w-full bg-black justify-center items-center">
                    <template
                      v-if="
                        ['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd)
                      "
                    >
                      <CameraOffGray
                        class="w-32 h-56 text-gray-500"
                        v-show="!isActiveCameraDevice"
                      ></CameraOffGray>
                      <video
                        autoplay
                        muted
                        ref="videoRef"
                        :class="['h-full w-full mirrored']"
                        v-show="isActiveCameraDevice"
                      ></video>
                    </template>
                    <!-- Show if device not camera -->
                    <template v-else>
                      <div
                        class="w-full bg-black py-4 qrCode"
                        v-show="!shouldPreviewExternalDevice"
                      >
                        <Qrcode :sessionId="sessionId" class="bg-white p-2 max-w-min mx-auto" />
                      </div>

                      <LocalVideoDisplay
                        :showTitle="false"
                        :showControls="false"
                        :isExternalPreview="true"
                        :cameraType="activeExternalDevicePreview"
                        :setVideoRef="multiCameraDevicesStore.setPreviewVideoRef"
                        :setAudioRef="multiCameraDevicesStore.setPreviewAudioRef"
                        v-show="shouldPreviewExternalDevice"
                      />
                    </template>
                  </div>

                  <!-- Show if device camera -->
                  <div
                    class="flex justify-between items-center mt-4"
                    v-if="['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd)"
                  >
                    <ViceButton data-cy="cancel-external-device" white @click="cancelSelection">
                      Cancel
                    </ViceButton>
                    <ViceButton
                      data-cy="save-external-device"
                      @click="buttonActions[multiCameraDevicesStore.multiDeviceToAdd]"
                      :disabled="isDisabledButton"
                    >
                      {{
                        CONNECT_EXTERNAL_DEVICE_MODAL_LABELS[
                          multiCameraDevicesStore.multiDeviceToAdd
                        ]?.buttonLabel
                      }}
                    </ViceButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import vClickOutside from 'click-outside-vue3';
import { ChevronDownIcon, XIcon } from '@heroicons/vue/solid';
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import Qrcode from '@/Components/QRcode.vue';
import useSessionStore from '@/Stores/useSessionStore';
import {
  MEDIA_TYPE,
  CAMERA_TYPES,
  DEVICE_NAME,
  DISPLAY_NAME,
  CONNECT_EXTERNAL_DEVICE_MODAL_LABELS,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import CameraOffGray from '@/Components/Icons/CameraOffGray.vue';
import LocalVideoDisplay from '@/Components/LocalVideoDisplay.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';

export default {
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    ViceButton,
    ChevronDownIcon,
    XIcon,
    Qrcode,
    CameraOffGray,
    LocalVideoDisplay,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    open: {
      type: Boolean,
    },
  },

  setup(props, { emit }) {
    const videoRef = ref(null);
    const videoReff = ref(null);
    const modalRef = ref(null);
    const isVisibleCameraList = ref(false);
    const isActiveCameraDevice = ref(false);
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const sessionStore = useSessionStore();
    const sessionId = sessionStore.sessionId;

    const selectedCameraType = computed(() => multiCameraDevicesStore.currentActiveCameraType);
    const shouldAddCamera = computed(
      () =>
        ['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd) &&
        isActiveCameraDevice.value,
    );
    const videoDevices = computed(
      () => multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO[DEVICE_NAME.CAMERA],
    );

    const selectedVideoDeviceLabel = computed(
      () => multiCameraDevicesStore.activeDeviceLabels[DEVICE_NAME.CAMERA],
    );

    const cameraVideoTrack = computed(
      () =>
        ['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd) &&
        multiCameraDevicesStore.allCameraTypes[selectedCameraType.value]?.localTracks?.find(
          (track) => track.getType() === MEDIA_TYPE.VIDEO,
        ),
    );

    watch(cameraVideoTrack, (newVideoTrack) => {
      if (newVideoTrack) newVideoTrack.attach(videoRef.value);
    });

    const closeModal = () => {
      emit('close');
    };

    const chooseCameraSource = (prop) => {
      multiCameraDevicesStore.chooseMediaSource(selectedCameraType.value, DEVICE_NAME.CAMERA, prop);
      isVisibleCameraList.value = false;
      isActiveCameraDevice.value = true;
    };

    const handleDropdownsState = (prop) => {
      isVisibleCameraList.value = !!prop && !isVisibleCameraList.value;
    };

    const addCamera = () => {
      if (shouldAddCamera.value) {
        multiCameraDevicesStore.toggleCameraTypeActiveState(selectedCameraType.value, true);
        // Updated connected camera only when a user selects it and it's an active camera
        multiCameraDevicesStore.connectedCameras = _.map(
          multiCameraDevicesStore.cameraTypes,
          (cameraType, cameraName) => ({
            name: cameraName,
            cameraDeviceId: cameraType.cameraDeviceId,
          }),
        );

        // If we are adding it in the live consultation phase,
        // create jitsi instance and add it to the call
        if (
          sessionStore.currentPhase == VICE_SESSION_PHASES.CONSULTATION &&
          sessionStore.hasLivePhase
        ) {
          multiCameraDevicesStore.initializeJitsi(sessionStore.sessionId);
        }
      }

      multiCameraDevicesStore.markSessionSetupAsChanged();
      multiCameraDevicesStore.desactivateDeviceSelection();
      multiCameraDevicesStore.setCurrentActiveCameraType(CAMERA_TYPES.INTERNAL_WEBCAM);

      closeModal();
    };

    const cancelSelection = () => {
      if (['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd)) {
        multiCameraDevicesStore.toggleCameraTypeActiveState(selectedCameraType.value, false);
        multiCameraDevicesStore.disableMultiCameraStream();
        multiCameraDevicesStore.removeCamera(selectedCameraType.value);
        multiCameraDevicesStore.setCurrentActiveCameraType(CAMERA_TYPES.INTERNAL_WEBCAM);
      }

      if (multiCameraDevicesStore.multiDeviceToAdd === 'smartGlass') {
        multiCameraDevicesStore.removeExternalDevicePreview(DISPLAY_NAME.VUZIX);
      }

      multiCameraDevicesStore.showPreviewModal = false;
    };

    // Click outside is even trigged when clicking on the dropdown so we
    // have this check to only cancel selection when we click outside of the selection div
    // i.e the id is headlessui-dialog-overlay-*.
    // This is also needed to prevent having multiple external cameras added. When this modal is opened,
    // We automatically add the device details to the store in anticipation for the preview. If the user doesn't
    // go ahead to add the camera or cancels the selected camera, we clear it from the store.
    onClickOutside(modalRef, (event) => {
      if (event.target.id.includes('headlessui-dialog-overlay')) cancelSelection();
    });

    const addSmartGlass = () => {
      multiCameraDevicesStore.updateExternalDevicePreview(DISPLAY_NAME.VUZIX, 'isActive', true);
      closeModal();
    };
    const addStethoscope = () => {
      multiCameraDevicesStore.updateExternalDevicePreview(
        DISPLAY_NAME.STETHOSCOPE,
        'isActive',
        true,
      );
      closeModal();
    };

    const buttonActions = {
      Camera: addCamera,
      smartGlass: addSmartGlass,
      stethoscope: addStethoscope,
      Monitor: addCamera,
    };

    const activeExternalDevicePreview = computed(
      () =>
        multiCameraDevicesStore.allExternalDevicePreview[
          CONNECT_EXTERNAL_DEVICE_MODAL_LABELS[multiCameraDevicesStore.multiDeviceToAdd].displayName
        ],
    );
    const shouldPreviewExternalDevice = computed(
      () => activeExternalDevicePreview.value?.tracks?.length,
    );

    const isDisabledButton = computed(() =>
      ['Camera', 'Monitor'].includes(multiCameraDevicesStore.multiDeviceToAdd)
        ? !shouldAddCamera.value
        : !shouldPreviewExternalDevice.value,
    );

    const autoAddAndCloseSmartGlass = () => {
      if (
        shouldPreviewExternalDevice.value > 1 &&
        activeExternalDevicePreview.value.name === DISPLAY_NAME.VUZIX
      ) {
        setTimeout(() => {
          addSmartGlass();
        }, 2000);
      }
    };

    // If smartglass is scanned for the first time. auto add after a 2secs delay
    watch(shouldPreviewExternalDevice, (isPreview) => {
      if (isPreview) autoAddAndCloseSmartGlass();
    });

    // Automatically add smart glass when it's connected
    autoAddAndCloseSmartGlass();

    return {
      isDisabledButton,
      modalRef,
      multiCameraDevicesStore,
      closeModal,
      selectedCameraType,
      addCamera,
      CAMERA_TYPES,
      sessionId,
      chooseCameraSource,
      isVisibleCameraList,
      handleDropdownsState,
      cancelSelection,
      DEVICE_NAME,
      videoDevices,
      videoRef,
      videoReff,
      selectedVideoDeviceLabel,
      isActiveCameraDevice,
      shouldAddCamera,
      buttonActions,
      CONNECT_EXTERNAL_DEVICE_MODAL_LABELS,
      shouldPreviewExternalDevice,
      activeExternalDevicePreview,
    };
  },
};
</script>
